import React, { Fragment } from "react";
import {
  Currency,
  Language,
  ModalSubComponent,
  ProductInterface,
  Segment,
  User,
} from "../../types/common.types";
import { useDispatch, useSelector } from "react-redux";
import { MODAL } from "./helpers/constants";
import WriteProduct from "./components/WriteProduct";
import { DeleteDrink, DeleteFood } from "../../helpers/xhrHelper";
import { ProductType } from "../product";
import { setModalState } from "../../redux/slices/AppState";
import { useNavigate } from "react-router-dom";
import { get } from "lockr";
import CustomButton from "../common/CustomButton";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import { XHR_STATE } from "../../helpers/xhr";
import { RootState } from "../../redux/store";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

interface TabInterface extends ModalSubComponent {}

const ProductTab = (props: TabInterface): JSX.Element => {
  const staffPermissions = get("staff-permissions") as string[] | undefined;

  const dispatch = useDispatch();
  const { xhr } = useSelector((state: RootState) => state.App);

  const cancelTranslation = useTranslation("cancel_translation");
  const editTranslation = useTranslation("edit_btn_text");
  const deleteTranslation = useTranslation("delete");

  const { activeProduct, modalMode, productType } = props;
  const editMode = modalMode === MODAL.EDIT;
  const EditOrCancelButton = editMode
    ? `${cancelTranslation}`
    : `${editTranslation}`;
  const navigate = useNavigate();
  let defaultLanguage = "en";
  let languages: Language[] = [];
  let currency: Currency = { name: "", prefix: "" };
  let segments: Segment[] = [];
  const userData = get("user") as User | undefined;
  if (userData) {
    defaultLanguage = userData.defaultLanguage;
    languages = userData.languages || [];
    currency = userData.currency;
    segments = userData.segments || [];
  }

  const productDeleteHandler = () => {
    if (productType === ProductType.Drink)
      DeleteDrink(activeProduct, dispatch, navigate);
    if (productType === ProductType.Food)
      DeleteFood(activeProduct, dispatch, navigate);
  };

  return (
    <Fragment>
      {(!staffPermissions || staffPermissions.includes("product_write")) && (
        <Box
          component="div"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            gap: 0.75,
            my: 1,
          }}
        >
          <CustomButton
            height="34px"
            size="small"
            sx={{
              width: "auto",
              minWidth: "86px",
              px: 1.4,
              borderRadius: "999px",
              background: "rgba(31, 22, 21, 0.86)",
              color: "#d9d0c8",
              border: "1px solid rgba(131, 60, 65, 0.22)",
              boxShadow: "none",
              fontSize: "12px",
              fontWeight: 700,
              "&:hover": {
                background: "rgba(131, 60, 65, 0.16)",
                borderColor: "rgba(158, 74, 80, 0.42)",
              },
            }}
            onClick={() =>
              dispatch(
                setModalState(
                  modalMode === MODAL.EDIT ? MODAL.VIEW : MODAL.EDIT
                )
              )
            }
          >
            {EditOrCancelButton}
          </CustomButton>
          <CustomButton
            height="34px"
            size="small"
            sx={{
              width: "auto",
              minWidth: "86px",
              px: 1.4,
              borderRadius: "999px",
              boxShadow: "none",
              fontSize: "12px",
              fontWeight: 700,
              color: "#f3d7d7",
              border: "1px solid rgba(248, 113, 113, 0.22)",
              background: "rgba(127, 29, 29, 0.2)",
              "&:hover": {
                background: "rgba(127, 29, 29, 0.32)",
                borderColor: "rgba(248, 113, 113, 0.34)",
              },
            }}
            background="rgba(127, 29, 29, 0.2)"
            onClick={() => productDeleteHandler()}
          >
            {xhr === XHR_STATE.REQUESTING ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              deleteTranslation
            )}
          </CustomButton>
        </Box>
      )}
      {modalMode === MODAL.VIEW ? (
        <ProductProfile
          product={activeProduct}
          productType={productType}
          defaultLanguage={defaultLanguage}
          languages={languages}
          currency={currency}
          segments={segments}
        />
      ) : (
        <WriteProduct
          modalMode={modalMode}
          product={activeProduct}
          productType={props.productType || 0}
        />
      )}
    </Fragment>
  );
};

interface ProductProfileProps {
  product: ProductInterface;
  productType: number;
  defaultLanguage: string;
  languages: Language[];
  currency: Currency;
  segments: Segment[];
}

type ResolvedPrice = {
  segment: Segment;
  price: number;
  source: "base" | "override" | "auto" | "missing";
};

const ProductProfile = ({
  product,
  productType,
  defaultLanguage,
  languages,
  currency,
  segments,
}: ProductProfileProps) => {
  const productName =
    product.names?.[defaultLanguage] || product.names?.en || "Untitled product";
  const productDescription =
    product.descriptions?.[defaultLanguage] ||
    product.descriptions?.en ||
    "No description provided.";
  const regularPrice = Number(product.prices?.regular || 0);
  const productPhoto = product.productPhoto?.secureUrl;
  const explicitOverrideCount = segments.filter(
    (segment) =>
      !isDefaultSegment(segment) && product.prices?.[segment.name] !== undefined
  ).length;
  const autoPriceCount = segments.filter(
    (segment) =>
      !isDefaultSegment(segment) && product.prices?.[segment.name] === undefined
  ).length;
  const resolvedPrices = resolveSegmentPrices(product, segments);
  const availableLanguages = languages.length
    ? languages
    : Object.keys(product.names || {}).map((iso) => ({ iso, name: iso }));

  return (
    <Stack spacing={1.6}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: "12px",
          bgcolor: "#16100f",
          border: "1px solid rgba(131, 60, 65, 0.18)",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "240px minmax(0, 1fr)" },
            minHeight: 220,
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 190, md: "100%" },
              bgcolor: "#0f0b0b",
              position: "relative",
            }}
          >
            {productPhoto ? (
              <Box
                component="img"
                src={productPhoto}
                alt={productName}
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: { xs: 190, md: 220 },
                  objectFit: "cover",
                  display: "block",
                  filter: product.inStock ? "none" : "grayscale(0.65)",
                }}
              />
            ) : (
              <Box
                sx={{
                  minHeight: { xs: 190, md: 220 },
                  display: "grid",
                  placeItems: "center",
                  color: "#7a6560",
                  fontSize: "12px",
                }}
              >
                No product photo
              </Box>
            )}
            <Chip
              size="small"
              icon={<Inventory2OutlinedIcon sx={{ fontSize: "14px" }} />}
              label={product.inStock ? "In stock" : "Out of stock"}
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: product.inStock
                  ? "rgba(74, 222, 128, 0.16)"
                  : "rgba(248, 113, 113, 0.16)",
                color: product.inStock ? "#4ade80" : "#f87171",
                border: `1px solid ${
                  product.inStock
                    ? "rgba(74, 222, 128, 0.28)"
                    : "rgba(248, 113, 113, 0.28)"
                }`,
                "& .MuiChip-icon": {
                  color: "inherit",
                },
              }}
            />
          </Box>

          <Stack spacing={1.6} sx={{ p: { xs: 1.6, md: 2 } }}>
            <Box>
              <Typography
                sx={{
                  color: "#f0ece8",
                  fontSize: { xs: "22px", md: "26px" },
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {productName}
              </Typography>
              <Typography
                sx={{
                  color: "#7a6560",
                  fontSize: "13px",
                  mt: 0.75,
                  lineHeight: 1.55,
                }}
              >
                {productDescription}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <InfoChip
                icon={<CategoryOutlinedIcon sx={{ fontSize: 15 }} />}
                label={product.category?.name || "Uncategorized"}
              />
              <InfoChip
                icon={<PaidOutlinedIcon sx={{ fontSize: 15 }} />}
                label={`${currency.prefix}${formatPrice(regularPrice)} regular`}
              />
              <InfoChip
                label={
                  segments.length
                    ? `${segments.length} segments · ${explicitOverrideCount} override · ${autoPriceCount} auto`
                    : "No segments configured"
                }
              />
              <InfoChip
                label={productType === ProductType.Drink ? "Drink" : "Food"}
              />
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1,
              }}
            >
              <MetricCard
                label="Regular price"
                value={`${currency.prefix}${formatPrice(regularPrice)}`}
              />
              <MetricCard
                label="Overrides"
                value={`${explicitOverrideCount}`}
                accent="#d5a84c"
              />
              <MetricCard label="Auto-priced" value={`${autoPriceCount}`} />
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
          },
          gap: 1.4,
        }}
      >
        <SectionCard title="Segment pricing">
          <Stack spacing={0.8}>
            {resolvedPrices.map(({ segment, price, source }) => (
              <Box
                key={segment.id || segment.name}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 1,
                  alignItems: "center",
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: "rgba(15, 11, 11, 0.48)",
                  border: "1px solid rgba(131, 60, 65, 0.12)",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#f0ece8",
                      fontSize: "13px",
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {segment.name}
                  </Typography>
                  <Typography sx={{ color: "#7a6560", fontSize: "11px" }}>
                    {priceSourceLabel(source, segment)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: source === "missing" ? "#f87171" : "#f0ece8",
                    fontFamily:
                      '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                    fontSize: "13px",
                    fontWeight: 700,
                  }}
                >
                  {source === "missing"
                    ? "Needs regular"
                    : `${currency.prefix}${formatPrice(price)}`}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SectionCard>

        <SectionCard title="Translations">
          <Stack spacing={1}>
            {availableLanguages.map((language) => {
              const name =
                product.names?.[language.iso] || product.names?.en || "";
              const description =
                product.descriptions?.[language.iso] ||
                product.descriptions?.en ||
                "";

              return (
                <Box
                  key={language.iso}
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    bgcolor: "rgba(15, 11, 11, 0.48)",
                    border: "1px solid rgba(131, 60, 65, 0.12)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <TranslateOutlinedIcon
                      sx={{ color: "#d5a84c", fontSize: 15 }}
                    />
                    <Typography
                      sx={{
                        color: "#f0ece8",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {language.name}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      color: "#f0ece8",
                      fontSize: "13px",
                      fontWeight: 600,
                      mt: 0.8,
                    }}
                  >
                    {name || "No name"}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#7a6560",
                      fontSize: "12px",
                      mt: 0.35,
                      lineHeight: 1.45,
                    }}
                  >
                    {description || "No description"}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </SectionCard>
      </Box>
    </Stack>
  );
};

const InfoChip = ({
  label,
  icon,
}: {
  label: string;
  icon?: React.ReactElement;
}) => (
  <Chip
    size="small"
    icon={icon}
    label={label}
    sx={{
      bgcolor: "rgba(31, 22, 21, 0.82)",
      color: "#d9d0c8",
      border: "1px solid rgba(131, 60, 65, 0.18)",
      fontSize: "11px",
      "& .MuiChip-icon": {
        color: "#d5a84c",
      },
    }}
  />
);

const MetricCard = ({
  label,
  value,
  accent = "#f0ece8",
}: {
  label: string;
  value: string;
  accent?: string;
}) => (
  <Box
    sx={{
      p: 1,
      borderRadius: "8px",
      bgcolor: "rgba(15, 11, 11, 0.52)",
      border: "1px solid rgba(131, 60, 65, 0.14)",
    }}
  >
    <Typography sx={{ color: "#7a6560", fontSize: "10px" }}>{label}</Typography>
    <Typography
      sx={{
        color: accent,
        fontSize: "16px",
        fontWeight: 700,
        mt: 0.25,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.3,
      borderRadius: "12px",
      bgcolor: "rgba(31, 22, 21, 0.72)",
      border: "1px solid rgba(131, 60, 65, 0.16)",
      boxShadow: "none",
    }}
  >
    <Typography
      variant="overline"
      sx={{
        color: "#7a6560",
        fontFamily:
          '"DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
        fontSize: "10px",
        letterSpacing: "0.12em",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ borderColor: "rgba(131, 60, 65, 0.14)", mb: 1 }} />
    {children}
  </Paper>
);

function resolveSegmentPrices(
  product: ProductInterface,
  segments: Segment[]
): ResolvedPrice[] {
  const productPrices = product.prices || {};
  const regularPrice = Number(productPrices.regular || 0);

  return segments.map((segment) => {
    if (isDefaultSegment(segment)) {
      return {
        segment,
        price: regularPrice,
        source: regularPrice > 0 ? "base" : "missing",
      };
    }

    const explicitPrice = productPrices[segment.name];
    if (explicitPrice !== undefined && Number(explicitPrice) > 0) {
      return {
        segment,
        price: Number(explicitPrice),
        source: "override",
      };
    }

    if (regularPrice > 0) {
      return {
        segment,
        price: roundPrice(regularPrice * (segment.priceMultiplier ?? 1)),
        source: "auto",
      };
    }

    return {
      segment,
      price: 0,
      source: "missing",
    };
  });
}

function isDefaultSegment(segment: Segment) {
  return segment.isDefault || segment.name.toLowerCase() === "regular";
}

function priceSourceLabel(source: ResolvedPrice["source"], segment: Segment) {
  if (source === "base") return "Base price · multiplier 1x";
  if (source === "override") return "Manual override";
  if (source === "auto")
    return `Auto from regular × ${segment.priceMultiplier ?? 1}`;
  return "Regular price is missing";
}

function roundPrice(price: number) {
  return Math.round((price + Number.EPSILON) * 100) / 100;
}

function formatPrice(price: number) {
  return Number(price || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default ProductTab;
